import React, { useMemo, useRef } from "react";
import { useContainerWidth } from "src/components/Hooks/useContainerWidth";

interface IXAxisScaleProps {
	width?: number;
	paddingLeft?: number;
	paddingRight?: number;
	domain: { min: number; max: number };
	labelFormat: string;
	formatDataLabel: (value: number, format: string, isAxisLabel: boolean) => string;
	tickCount?: number;
	ticksCentered?: boolean;
}

export const XAxisScale: React.FC<IXAxisScaleProps> = ({
	width: widthProp,
	paddingLeft = 0,
	paddingRight = 15,
	domain,
	labelFormat,
	formatDataLabel,
	tickCount = 2,
	ticksCentered = true,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const measuredWidth = useContainerWidth(containerRef);

	const resolvedWidth = measuredWidth || widthProp || 0;

	const ticks = useMemo(() => {
		const { min, max } = domain;
		if (min < 0 && max > 0) {
			return [min, 0, max].map((value) => ({
				value,
				label: formatDataLabel(value, labelFormat, true),
			}));
		}
		const range = max - min;
		const step = range / (tickCount - 1);

		return Array.from({ length: tickCount }, (_, i) => {
			const value = min + step * i;
			return {
				value,
				label: formatDataLabel(value, labelFormat, true),
			};
		});
	}, [domain, labelFormat, formatDataLabel, tickCount]);

	const cssPaddingLeft = Math.max(paddingLeft, 0);
	const leftOffset = Math.min(paddingLeft, 0);
	const effectiveWidth = resolvedWidth - paddingLeft - paddingRight;
	const range = domain.max - domain.min;

	return (
		<div
			ref={containerRef}
			className="relative"
			style={{
				width: "100%",
				height: 32,
				paddingLeft: cssPaddingLeft,
				paddingRight,
				overflow: "visible",
			}}>
			{effectiveWidth > 0 && (
				<div className="relative w-full h-full" style={{ width: effectiveWidth, marginLeft: leftOffset }}>
					{ticks.map((tick, idx) => {
						const position =
							range > 0
								? ((tick.value - domain.min) / range) * effectiveWidth
								: (idx / (ticks.length - 1)) * effectiveWidth;
						const isFirst = idx === 0;
						const shiftFirst = !ticksCentered && isFirst && domain.min >= 0;

						return (
							<div
								key={idx}
								className="absolute flex flex-col items-center"
								style={{
									left: position,
									transform: "translateX(-50%)",
									top: 0,
								}}>
								<div
									style={{
										width: 1,
										height: 6,
										backgroundColor: "#D6DFED",
									}}
								/>
								<span
									className="text-[10px] text-gray-500 mt-1 whitespace-nowrap"
									style={{
										fontVariantNumeric: "tabular-nums",
										// 0%: left-align so it doesn't overflow left
										...(shiftFirst ? { transform: "translateX(50%)" } : {}),
									}}>
									{tick.label}
								</span>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default XAxisScale;
